import pluralize from "pluralize";
import type { z } from "zod";
import {
  getInAppReconcileIntervalMs,
  nextInAppReconcileSlot,
} from "@hightouch/lib/cep/campaign-schedule";

import {
  ScheduleIntervalUnit,
  ScheduleType,
  type InAppMessageExpiration,
  type IntervalSchedule,
  type Schedule,
} from "src/components/schedule/types";

export const IN_APP_INTERVAL_UNIT_OPTIONS: {
  label: string;
  value: ScheduleIntervalUnit;
}[] = [
  { label: "minutes", value: ScheduleIntervalUnit.MINUTE },
  { label: "hours", value: ScheduleIntervalUnit.HOUR },
  { label: "days", value: ScheduleIntervalUnit.DAY },
  { label: "weeks", value: ScheduleIntervalUnit.WEEK },
];

export const DEFAULT_IN_APP_INTERVAL = {
  quantity: 1,
  unit: ScheduleIntervalUnit.HOUR,
} as const;

export type InAppRefreshCadence = {
  value: string;
  /** Menu row text, read under the menu's own "Refresh the audience every…"
   *  header. The closed select shows `describeCadence` instead. */
  label: string;
  quantity: number;
  unit: ScheduleIntervalUnit;
};

/**
 * The cadences the audience drawer offers. Deliberately narrower than the
 * free-form quantity + unit the schedule still stores: a campaign whose
 * interval predates this list keeps it, offered alongside as its own option.
 */
export const IN_APP_REFRESH_CADENCES: InAppRefreshCadence[] = [
  {
    value: "15-minute",
    label: "15 minutes",
    quantity: 15,
    unit: ScheduleIntervalUnit.MINUTE,
  },
  {
    value: "1-hour",
    label: "Hour",
    quantity: 1,
    unit: ScheduleIntervalUnit.HOUR,
  },
  { value: "1-day", label: "Day", quantity: 1, unit: ScheduleIntervalUnit.DAY },
  {
    value: "1-week",
    label: "Week",
    quantity: 1,
    unit: ScheduleIntervalUnit.WEEK,
  },
];

/**
 * A campaign already on an interval the list doesn't offer keeps it, appended
 * as its own option, so opening the drawer can't quietly rewrite the cadence to
 * whichever preset happens to sort first.
 */
export function offeredCadences(
  schedule: Schedule | null | undefined,
): InAppRefreshCadence[] {
  const { quantity, unit } = getInAppInterval(schedule);
  if (!quantity || !unit || matchRefreshCadence(schedule)) {
    return IN_APP_REFRESH_CADENCES;
  }
  return [
    ...IN_APP_REFRESH_CADENCES,
    {
      value: cadenceValue(quantity, unit),
      label: describeCadence({ quantity, unit }),
      quantity,
      unit,
    },
  ];
}

export function cadenceValue(
  quantity: number,
  unit: ScheduleIntervalUnit,
): string {
  return `${quantity}-${unit}`;
}

/**
 * The cadence a schedule currently sits on. A schedule with no interval, or one
 * outside the offered list, returns undefined so the caller can leave the select
 * empty or offer the stored value rather than silently snapping to a preset.
 */
export function matchRefreshCadence(
  schedule: Schedule | null | undefined,
): InAppRefreshCadence | undefined {
  const { quantity, unit } = getInAppInterval(schedule);
  if (!quantity || !unit) return undefined;
  const value = cadenceValue(quantity, unit);
  return IN_APP_REFRESH_CADENCES.find((cadence) => cadence.value === value);
}

export function isInAppSchedule(
  schedule: Schedule | null | undefined,
): schedule is IntervalSchedule {
  return schedule?.type === ScheduleType.INTERVAL;
}

export function getInAppInterval(schedule: Schedule | null | undefined): {
  quantity?: number | null;
  unit?: ScheduleIntervalUnit;
} {
  return isInAppSchedule(schedule) ? (schedule.schedule?.interval ?? {}) : {};
}

export function createInAppSchedule(): IntervalSchedule {
  return {
    type: ScheduleType.INTERVAL,
    schedule: { interval: { ...DEFAULT_IN_APP_INTERVAL } },
  };
}

export function withInAppInterval(
  schedule: Schedule | null | undefined,
  interval: { quantity?: number | null; unit?: ScheduleIntervalUnit },
): IntervalSchedule {
  const base = isInAppSchedule(schedule) ? schedule : createInAppSchedule();
  return {
    ...base,
    type: ScheduleType.INTERVAL,
    schedule: { interval: { ...getInAppInterval(base), ...interval } },
  };
}

/** How a cadence reads wherever it is shown as a value rather than a choice. */
export function describeCadence(interval: {
  quantity: number;
  unit: ScheduleIntervalUnit;
}): string {
  const { quantity, unit } = interval;
  return quantity === 1
    ? `Every ${unit}`
    : `Every ${quantity} ${pluralize(unit, quantity)}`;
}

export function describeInAppInterval(
  schedule: Schedule | null | undefined,
): string | null {
  const { quantity, unit } = getInAppInterval(schedule);
  if (!quantity || !unit) return null;
  return describeCadence({ quantity, unit });
}

type SchedulingData = {
  campaignType?: string | null;
  scheduleConfig?: Record<string, unknown> | null;
};

/**
 * Cadence only. The drawer pins in-app to `CampaignEndMode.SpecificDate`, so
 * `refineCampaignScheduling` already requires the end date.
 */
export function refineInAppScheduling(
  data: SchedulingData,
  ctx: z.RefinementCtx,
): void {
  if (data.campaignType !== "in_app") return;

  const schedule = data.scheduleConfig as Schedule | null | undefined;

  const { quantity, unit } = getInAppInterval(schedule);
  if (!quantity || quantity < 1 || !unit) {
    ctx.addIssue({
      code: "custom",
      message: "Set how often the audience is re-evaluated",
      path: ["scheduleConfig"],
    });
  }

  // Absent means "never expires", which is valid. Present but incomplete is
  // what the marketer is mid-way through and must not save.
  const expiration = getMessageExpiration(schedule);
  if (expiration && (!expiration.quantity || expiration.quantity < 1)) {
    ctx.addIssue({
      code: "custom",
      message: "Enter how long the message lasts",
      path: ["scheduleConfig"],
    });
  }
}

export const DEFAULT_IN_APP_MESSAGE_EXPIRATION: InAppMessageExpiration = {
  quantity: 7,
  unit: ScheduleIntervalUnit.DAY,
};

export function getMessageExpiration(
  schedule: Schedule | null | undefined,
): InAppMessageExpiration | null {
  return schedule?.messageExpiration ?? null;
}

/**
 * Set or clear the campaign's start, keeping every other field. A campaign with
 * no schedule yet gets the in-app shape rather than a bare object, so the result
 * is always a legal Schedule.
 */
export function withInAppStart(
  schedule: Schedule | null | undefined,
  start: { startDate: string; startTime: string } | null,
): Schedule {
  const base = isInAppSchedule(schedule) ? schedule : createInAppSchedule();
  return start
    ? { ...base, ...start }
    : { ...base, startDate: null, startTime: undefined };
}

export function withMessageExpiration(
  schedule: Schedule | null | undefined,
  expiration: InAppMessageExpiration | null,
): Schedule {
  const base = isInAppSchedule(schedule) ? schedule : createInAppSchedule();
  return { ...base, messageExpiration: expiration };
}

/**
 * How the expiration reads on the overview card. Null when nothing expires, so
 * the caller shows the campaign's own end instead of inventing a duration.
 */
export function describeMessageExpiration(
  schedule: Schedule | null | undefined,
): string | null {
  const expiration = getMessageExpiration(schedule);
  if (!expiration?.quantity || !expiration.unit) return null;
  return `After ${expiration.quantity} ${pluralize(
    expiration.unit,
    expiration.quantity,
  )}`;
}

/**
 * When the scheduler starts the next reconcile: the first interval slot after
 * `lastSlotAt` that the last run did not overlap. Falls back to the run's
 * creation when the campaign has no recorded slot.
 */
export function nextInAppRefreshAt(
  schedule: Schedule | null | undefined,
  lastRun: { created_at: string; completed_at?: string | null },
  lastSlotAt: string | null | undefined,
): Date | null {
  const intervalMs = schedule ? getInAppReconcileIntervalMs(schedule) : null;
  if (!intervalMs) return null;
  const slot = Date.parse(lastSlotAt ?? lastRun.created_at);
  if (Number.isNaN(slot)) return null;
  const completed = lastRun.completed_at
    ? Date.parse(lastRun.completed_at)
    : Number.NaN;
  return nextInAppReconcileSlot({
    intervalMs,
    lastSlotAt: new Date(slot),
    previousRunCompletedAt: Number.isNaN(completed)
      ? null
      : new Date(completed),
  });
}
